import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { getPostBySlug, formatDate } from "@/lib/posts";
import type { Post } from "@/lib/posts";
import { CTAButton, mailto } from "@/components/site/CTAButton";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }): Promise<{ post: Post }> => {
    // Old India-focused URL, content retargeted to US companies.
    if (params.slug === "eutm-filing-for-indian-companies-procedure-fees-timeline") {
      throw redirect({
        to: "/blog/$slug",
        params: { slug: "eutm-filing-for-us-companies-procedure-fees-timeline" },
        statusCode: 301,
      });
    }
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [{ title: "Article — Bauer IP" }],
        links: [{ rel: "canonical", href: "https://bauerip.com/blog" }],
      };
    }
    return {
      meta: [
        { title: `${post.title} — Bauer IP` },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: post.title },
        { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
        { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
        { property: "og:description", content: post.excerpt },
        { property: "og:url", content: `https://bauerip.com/blog/${post.slug}` },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
        { name: "article:published_time", content: post.date },
        ...(post.updatedDate ? [{ name: "article:modified_time", content: post.updatedDate }] : []),
      ],
      links: [
        { rel: "canonical", href: `https://bauerip.com/blog/${post.slug}` },
        { rel: "alternate", hrefLang: "en-US", href: `https://bauerip.com/blog/${post.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: `https://bauerip.com/blog/${post.slug}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            dateModified: post.updatedDate ?? post.date,
            author: {
              "@type": "Person",
              name: "Karel Bauer",
            },
            publisher: {
              "@type": "Organization",
              name: "Bauer IP",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `/blog/${post.slug}`,
            },
          }),
        },
      ],
    };
  },
  component: BlogPost,
  notFoundComponent: PostNotFound,
  errorComponent: PostError,
});

function renderParagraph(text: string) {
  const parts: (string | React.ReactNode)[] = [];
  let lastIndex = 0;
  // Matches [label](url) for absolute and relative links, plus **bold** runs.
  const regex = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)|\*\*([^*]+)\*\*/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[3]) {
      parts.push(
        <strong key={match.index} className="text-foreground font-medium">
          {match[3]}
        </strong>,
      );
    } else {
      const href = match[2];
      parts.push(
        href.startsWith("/") ? (
          <Link key={match.index} to={href} className="underline hover:text-foreground">
            {match[1]}
          </Link>
        ) : (
          <a
            key={match.index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            {match[1]}
          </a>
        ),
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : <>{parts}</>;
}


function BlogPost() {
  const { post } = Route.useLoaderData();
  const isUpcReadiness = post.slug === "upc-opt-out-strategy-for-us-companies";
  const isEuUkTrademark = post.slug === "does-eu-trademark-cover-uk-us-brands-after-brexit";

  return (
    <article className="mx-auto max-w-3xl px-6 py-20 md:py-24">
      <div className="mb-10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="eyebrow text-xs">{post.category}</span>
          <time className="text-sm text-muted-foreground" dateTime={post.date}>
            {formatDate(post.date)}
          </time>
          {post.updatedDate && (
            <span className="text-sm text-muted-foreground">
              Updated <time dateTime={post.updatedDate}>{formatDate(post.updatedDate)}</time>
            </span>
          )}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl text-foreground leading-tight">
          {post.title}
        </h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>

        <div className="mt-8 rounded-lg border border-border bg-secondary/40 p-6">
          <p className="text-sm text-foreground font-medium">
            {isEuUkTrademark
              ? "Check your EU brand protection and identify any separate UK coverage gap before launch."
              : isUpcReadiness
              ? "Review UPC jurisdiction, ownership and your European portfolio before a dispute begins."
              : "Planning a European filing from the US? Get a fixed-fee estimate and a filing timeline before your deadline."}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <CTAButton
              variant="accent"
              trackLabel="book_consultation_us_filing"
              trackContext={`blog:${post.slug}:above_fold`}
              href={mailto({
                subject: isEuUkTrademark ? "EU trademark coverage consultation — US brand and UK market" : isUpcReadiness ? "UPC opt-out and readiness consultation" : "Book a consultation for my US filing in Europe",
                body: isEuUkTrademark
                  ? "Hello Karel,\n\nI'd like to discuss EU trademark protection and any separate UK coverage gap.\n\n— Company / owner:\n— Mark:\n— Goods and services:\n— Target EU markets and UK plans:\n— USPTO filing date and number:\n— Existing EUTM, UK or Madrid numbers:\n— Launch date:\n— Preferred time (US time zone):\n"
                  : isUpcReadiness
                  ? "Hello Karel,\n\nI'd like to discuss UPC opt-out and portfolio readiness.\n\n— Company:\n— EP application/patent numbers:\n— Current owners:\n— Target European markets:\n— Existing opt-outs or known proceedings:\n— Preferred time (US time zone):\n\nI will share confidential material only after a conflict check and an agreed channel.\n"
                  : `Hello Karel,\n\nI'd like to book a consultation about taking our US filing into Europe.\n\n— Company:\n— US application/patent number:\n— Priority or PCT date:\n— Target European markets:\n— Preferred time (US time zone):\n\n(Article: ${post.title})\n`,
              })}
            >
              {isEuUkTrademark ? "Discuss EU trademark coverage" : isUpcReadiness ? "Discuss UPC readiness" : "Book a consultation for your US filing"}
            </CTAButton>
            {!isUpcReadiness && !isEuUkTrademark && <CTAButton
              variant="secondary"
              trackLabel="fixed_fee_quote"
              trackContext={`blog:${post.slug}:above_fold`}
              href={mailto({
                subject: `Fixed-fee quote — European filing (${post.category})`,
                body: `Hello Karel,\n\nPlease send a fixed-fee quote for European protection.\n\n— Company:\n— Technology / brand:\n— Deadline (Paris or 31-month PCT):\n— Countries of interest:\n\n(Article: ${post.title})\n`,
              })}
            >
              Get a fixed-fee quote
            </CTAButton>}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Direct reply from a {isEuUkTrademark ? "European Trade Mark Attorney" : "European Patent Attorney"} — usually within one business day.
          </p>
        </div>
      </div>

      <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-6">
        {post.table && (
          <div className="not-prose overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <caption className="text-left text-muted-foreground mb-3 text-base">
                {post.table.caption}
              </caption>
              <thead>
                <tr className="border-b border-border">
                  {post.table.headers.map((header: string, i: number) => (
                    <th key={i} className="py-3 pr-4 font-semibold text-foreground">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {post.table.rows.map((row: string[], i: number) => (
                  <tr key={i} className="border-b border-border/60">
                    {row.map((cell: string, j: number) => (
                      <td key={j} className="py-3 pr-4 text-muted-foreground">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {post.content.map((paragraph: string, i: number) => (
          <p key={i} className="text-muted-foreground leading-relaxed">
            {renderParagraph(paragraph)}
          </p>
        ))}
      </div>

      <div className="mt-16 pt-10 border-t border-border">
        {post.pillar && (
          <div className="mb-12 rounded-lg border border-border bg-secondary/40 p-8">
            <p className="eyebrow mb-3 text-xs">{post.pillar.eyebrow}</p>
            <h2 className="font-serif text-2xl text-foreground leading-tight">
              {post.pillar.title}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">
              {post.pillar.blurb}
            </p>
            <div className="mt-6">
              <Link
                to={post.pillar.href}
                className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {post.pillar.label} →
              </Link>
            </div>
          </div>
        )}
        <h2 className="font-serif text-2xl text-foreground leading-tight mb-4">
          Need advice on this topic?
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-6 max-w-xl">
          If you are planning European expansion, I can help you align patent, trade mark and timing
          strategy before launch.
        </p>
        <div className="flex flex-wrap gap-3">
          <CTAButton
            variant="accent"
            trackLabel="ask_a_question"
            trackContext={`blog:${post.slug}:footer`}
            href={mailto({
              subject: `Question about: ${post.title}`,
              body: "Hello Karel,\n\nI read your article and I'd like to discuss:\n\n— Company:\n— Website:\n— What I'm planning in Europe:\n",
            })}
          >
            Ask a Question
          </CTAButton>
          <CTAButton
            variant="secondary"
            trackLabel="book_a_call"
            trackContext={`blog:${post.slug}:footer`}
            href={mailto({
              subject: "Book a call — European IP strategy",
              body: "Hello Karel,\n\nI'd like to book a call to discuss European IP strategy.\n\n— Name:\n— Company:\n— Preferred time zone:\n",
            })}
          >
            Book a Call
          </CTAButton>
        </div>
      </div>
    </article>
  );
}

function PostNotFound() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl text-foreground">Article not found</h1>
      <p className="mt-4 text-muted-foreground">
        The article you&apos;re looking for doesn&apos;t exist or has been moved.
      </p>
      <div className="mt-8">
        <Link
          to="/blog"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Back to insights
        </Link>
      </div>
    </div>
  );
}

function PostError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  if (error) {
    console.error("Blog post render error:", error);
  }
  return (
    <div className="mx-auto max-w-6xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl text-foreground">This article didn&apos;t load</h1>
      <p className="mt-4 text-muted-foreground">
        This article couldn&apos;t be loaded. Please try again or return to the blog.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-2">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try again
        </button>
        <Link
          to="/blog"
          className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          Back to insights
        </Link>
      </div>
    </div>
  );
}
